import { ShieldCheck, Lock, Truck, Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { motion } from "framer-motion";
import logo from "@/assets/logo.webp";

const linkColumns = [
  {
    heading: "Company",
    links: [
      { label: "About Us", to: "/about" },
      { label: "Careers", to: "/careers" },
      { label: "Contact", to: "/about#contact" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "How LohaLink Works", to: "/how-it-works" },
      { label: "EPR Credit Brokerage", to: "/epr" },
      { label: "Market Insights", to: "/insights" },
      { label: "Blog / Journal", to: "/blog" },
      { label: "Live Scrap Rates", to: "/rates" },
      { label: "Help Center", to: "/about#help" },
    ],
  },
  {
    heading: "Top hubs",
    links: [
      { label: "Scrap yards in Durgapur", to: "/locations/durgapur" },
      { label: "Scrap yards in Siliguri", to: "/locations/siliguri" },
      { label: "Scrap yards in Raipur", to: "/locations/raipur" },
      { label: "Scrap yards in Rajkot", to: "/locations/rajkot" },
      { label: "All cities", to: "/locations" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Terms & Conditions", to: "/terms" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Escrow Rules", to: "/terms#escrow" },
    ],
  },
];


export const Footer = () => (
  <footer className="relative overflow-hidden border-t border-border bg-primary text-primary-foreground">
    {/* Subtle watermark logo */}
    <img
      src={logo}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-16 -right-10 h-[420px] w-[420px] select-none object-contain opacity-[0.06] md:-right-20 md:h-[560px] md:w-[560px]"
    />

    <div className="relative">
      <div className="container grid gap-8 py-10 md:grid-cols-3">
        <TrustItem
          icon={<ShieldCheck className="h-5 w-5 text-success" />}
          title="GST & MCA Verified"
          copy="Every yard and factory passes mandatory KYC and GSTIN verification before listing."
        />
        <TrustItem
          icon={<Lock className="h-5 w-5 text-accent" />}
          title="Escrow Secured"
          copy="Funds held in regulated escrow until material is loaded, weighed and accepted."
        />
        <TrustItem
          icon={<Truck className="h-5 w-5 text-info" />}
          title="Logistics Partner Network"
          copy="Pan-India fleet integration with live truck tracking and digital LR receipts."
        />
      </div>

      {/* Brand + nav link columns */}
      <div className="border-t border-primary-foreground/10 py-12">
        <div className="container grid gap-10 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          <div>
            <div className="flex items-center gap-2">
              <img src={logo} alt="LohaLink" className="h-9 w-9 object-contain" />
              <div className="text-xl font-extrabold tracking-tight">
                Loha<span className="text-accent">Link</span>
              </div>
            </div>
            <p className="mt-3 text-sm text-primary-foreground/70">
              India's Reliable Metal Gateway.
            </p>
            <div className="mt-4 text-xs uppercase tracking-wider text-accent">
              Metal Linked
            </div>
            <div className="mt-4 flex gap-3">
              <a
                href="https://www.facebook.com/profile.php?id=61563635854204"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/10 text-primary-foreground transition hover:bg-accent hover:text-accent-foreground"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href="https://www.instagram.com/iron_scrap.in?igsh=MWFldWF2Nmw4eGUxNg=="
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/10 text-primary-foreground transition hover:bg-accent hover:text-accent-foreground"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="https://www.linkedin.com/company/lohalink-in"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/10 text-primary-foreground transition hover:bg-accent hover:text-accent-foreground"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a
                href="https://x.com/lohalink_in"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/10 text-primary-foreground transition hover:bg-accent hover:text-accent-foreground"
              >
                <Twitter className="h-4 w-4" />
              </a>
            </div>
          </div>

          {linkColumns.map((col) => (
            <div key={col.heading}>
              <div className="text-sm font-semibold text-primary-foreground">
                {col.heading}
              </div>
              <ul className="mt-3 space-y-2">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm text-primary-foreground/70 transition hover:text-primary-foreground"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Big LohaLink wordmark + logo */}
      <div className="border-t border-primary-foreground/10 py-12">
        <div className="container flex flex-col items-center gap-4 text-center">
          <motion.img
            src={logo}
            alt="LohaLink logo"
            className="h-40 w-40 object-contain md:h-56 md:w-56"
            loading="lazy"
            initial={{ opacity: 0, scale: 0.6, rotate: -12 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ type: "spring", stiffness: 120, damping: 12 }}
            whileHover={{ scale: 1.08, rotate: [0, -4, 4, 0], transition: { duration: 0.6 } }}
          />
          <motion.div
            className="text-4xl font-extrabold tracking-tight md:text-6xl"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.4 }}
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
            }}
          >
            {"LohaLink".split("").map((ch, i) => (
              <motion.span
                key={i}
                className={i >= 4 ? "text-accent inline-block" : "inline-block"}
                variants={{
                  hidden: { opacity: 0, y: 24 },
                  visible: { opacity: 1, y: 0 },
                }}
                transition={{ type: "spring", stiffness: 200, damping: 14 }}
              >
                {ch}
              </motion.span>
            ))}
          </motion.div>
          <motion.div
            lang="sa"
            className="text-xs italic tracking-wide text-accent md:text-sm"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: false }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            Metal Linked
          </motion.div>
          <motion.p
            className="max-w-xl text-sm text-primary-foreground/70"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ delay: 0.8, duration: 0.5 }}
          >
            India&apos;s trust-first scrap metal marketplace. GST-verified
            yards, escrow-protected payments, live truck tracking — every
            transaction.
          </motion.p>
        </div>
      </div>

      <div className="border-t border-primary-foreground/10 py-4">
        <div className="container flex flex-col items-center justify-between gap-2 text-xs text-primary-foreground/85 md:flex-row">
          <span>
            © {new Date().getFullYear()} LohaLink — The Trusted Network for
            Bulk Scrap Metal Trading.
          </span>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-primary-foreground">
              Terms &amp; Conditions
            </Link>
            <Link to="/privacy" className="hover:text-primary-foreground">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  </footer>
);

const TrustItem = ({
  icon,
  title,
  copy,
}: {
  icon: React.ReactNode;
  title: string;
  copy: string;
}) => (
  <div className="flex gap-3">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-foreground/5">
      {icon}
    </div>
    <div>
      <div className="text-sm font-semibold">{title}</div>
      <p className="mt-1 text-sm text-primary-foreground/70">{copy}</p>
    </div>
  </div>
);
